<script setup>
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { usePricingRulesStore } from 'dashboard/stores/pricingRules';
import { createConsumer } from '@rails/actioncable';

const props = defineProps({
  pricingPlanId: { type: [String, Number], required: true },
  accountId: { type: [String, Number], required: true },
});

const store = useStore();
const pricingRulesStore = usePricingRulesStore();
const { t } = useI18n();

const planId = computed(() => Number(props.pricingPlanId));
const plan = computed(
  () => store.getters['pricingPlans/getPlanById'](planId.value) || {}
);
const rules = computed(() => pricingRulesStore.records);
const items = computed(() => store.getters['pricingItems/getRecords'] || []);

const enrichedRules = computed(() =>
  rules.value.map(rule => {
    const item = items.value.find(i => i.id === rule.rate_id);
    return {
      ...rule,
      itemName: item?.name || String(rule.rate_id),
      itemCategory: item?.category || 'other',
    };
  })
);

function fetchData() {
  store.dispatch('pricingPlans/show', planId.value);
  pricingRulesStore.get(planId.value);
  if (!items.value.length) store.dispatch('pricingItems/get');
}

// --- Action Cable subscription for live updates ---
let subscription = null;

function subscribeToCable() {
  const consumer = createConsumer();
  subscription = consumer.subscriptions.create(
    {
      channel: 'PricingPlanChannel',
      pricing_plan_id: planId.value,
      account_id: Number(props.accountId),
    },
    {
      received(data) {
        if (data.event === 'pricing_plan.updated') {
          fetchData();
        }
      },
    }
  );
}

function unsubscribeFromCable() {
  if (subscription) {
    subscription.unsubscribe();
    subscription = null;
  }
}

onMounted(() => {
  fetchData();
  subscribeToCable();
});

onUnmounted(() => {
  unsubscribeFromCable();
});

watch(planId, () => {
  unsubscribeFromCable();
  fetchData();
  subscribeToCable();
});

const categoryIcon = category => {
  const icons = {
    space: 'i-lucide-orbit',
    staff: 'i-lucide-users',
    catering: 'i-lucide-utensils',
    av: 'i-lucide-monitor',
    add_on: 'i-lucide-plus-circle',
    other: 'i-lucide-box',
  };
  return icons[category] || icons.other;
};
</script>

<template>
  <div class="flex flex-col h-full overflow-y-auto">
    <!-- Header -->
    <div class="px-4 py-3 border-b border-n-weak">
      <h2 class="text-base font-semibold text-n-slate-12 truncate">
        {{ plan.name || t('COPILOT.PANEL.LOADING') }}
      </h2>
      <div class="flex items-center gap-2 mt-1">
        <span
          v-if="plan.is_default"
          class="text-xs px-1.5 py-0.5 rounded bg-n-violet-3 text-n-violet-11"
        >
          Default
        </span>
        <span
          v-if="plan.active !== undefined"
          class="text-xs px-1.5 py-0.5 rounded"
          :class="
            plan.active
              ? 'bg-n-green-3 text-n-green-11'
              : 'bg-n-slate-3 text-n-slate-10'
          "
        >
          {{ plan.active ? 'Active' : 'Inactive' }}
        </span>
        <span
          v-if="plan.valid_from || plan.valid_to"
          class="text-xs text-n-slate-10"
        >
          {{ plan.valid_from || '∞' }} → {{ plan.valid_to || '∞' }}
        </span>
      </div>
      <p class="text-xs text-n-slate-9 mt-1">
        {{ t('COPILOT.PANEL.LIVE_UPDATES') }}
      </p>
    </div>

    <!-- Rules list -->
    <div class="flex-1 px-4 py-3 space-y-2">
      <h3
        class="text-xs font-medium text-n-slate-10 uppercase tracking-wide mb-2"
      >
        Rules ({{ enrichedRules.length }})
      </h3>
      <div
        v-for="rule in enrichedRules"
        :key="rule.id"
        class="flex items-center gap-2 p-2 rounded-lg bg-n-solid-2 ring-1 ring-n-weak"
      >
        <div class="size-4 shrink-0" :class="categoryIcon(rule.itemCategory)" />
        <div class="flex-1 min-w-0">
          <p class="text-sm text-n-slate-12 truncate">{{ rule.itemName }}</p>
          <p class="text-xs text-n-slate-10">
            {{ (rule.clauses || []).length }} clause{{
              (rule.clauses || []).length === 1 ? '' : 's'
            }}
          </p>
        </div>
        <span
          v-if="rule.is_default"
          class="text-[10px] px-1 py-0.5 rounded bg-n-slate-3 text-n-slate-9"
        >
          default
        </span>
      </div>
      <p
        v-if="enrichedRules.length === 0"
        class="text-sm text-n-slate-10 text-center py-4"
      >
        No rules yet
      </p>
    </div>
  </div>
</template>
